/**
 * WebSocket client for `/ws`. Mirrors WsClient.kt in the Android app:
 *   - sendStartSession / sendUttStart / sendUttEnd / sendSetOtherLang /
 *     sendEndSession / sendPcm
 *   - parses inbound JSON frames + the binary TTS frame that follows a `tts`
 *     header
 *   - emits typed events to a single subscriber
 *   - background keepalive every 75 s so the server's idle-close (4408)
 *     doesn't fire during natural pauses
 *
 * Quota / timeout close codes:
 *   4002 = superseded by another connection from the same user
 *   4408 = server-side session timeout (max duration or idle)
 *   4429 = quota exceeded — do NOT auto-reconnect
 */

import { getApiKey, resolveWs } from "./config";
import { wsTicket } from "./api";
import { authStore } from "./store";

export type Translations = Record<string, string>;

export type TurnSummary = {
  turn_idx: number;
  turn_id: number;
  label: string; // "owner" | "other"
  t_start: number;
  t_end: number;
};

export type WsEvent =
  | { type: "connected"; session_id: number }
  | { type: "utt_accepted"; utt_id: string; utterance_id: number; turns: TurnSummary[] }
  | { type: "partial"; role: string; src_text: string; translations: Translations }
  | {
      type: "final";
      role: string;
      turn_id: number;
      src_text: string;
      translations: Translations;
      detected_language: string | null;
      tts_pending: boolean;
    }
  | { type: "tts_header"; role: string; turn_id: number; byte_length: number }
  | { type: "tts_audio"; role: string; turn_id: number; wav: ArrayBuffer }
  | { type: "lang_changed"; current_other_lang: string }
  | { type: "error"; code: string; message: string }
  | { type: "session_ended"; session_id: number | null }
  | { type: "closed"; code: number; reason: string };

export class WsClient {
  private ws: WebSocket | null = null;
  private pendingTts: { role: string; turn_id: number; byte_length: number } | null = null;
  private keepaliveTimer: number | null = null;
  private uttActive = false;
  // The keepalive reuses `start_session` (below) to reset the server's idle
  // clock, and the server echoes `session_started` for each one. Only the
  // FIRST echo on this socket is a real connect — the rest are keepalive
  // pings and must NOT be surfaced as `connected`, or the UI would clear its
  // error banner and re-arm the mic every 75 s on its own.
  private connectAnnounced = false;

  constructor(private readonly onEvent: (e: WsEvent) => void) {}

  async open(): Promise<boolean> {
    this.connectAnnounced = false;
    if (!authStore.getState().tokens?.access_token) return false;
    // Exchange the (header-borne) access token for a short-lived, single-use
    // ticket so the long-lived JWT never rides the WS URL — query strings end
    // up in Caddy/proxy access logs. `wsTicket()` goes through `authed`, which
    // transparently refreshes on a 401, so an expired access token self-heals.
    let ticket: string;
    try {
      ticket = (await wsTicket()).ticket;
    } catch {
      return false;
    }
    const apiKey = getApiKey();
    const qs = new URLSearchParams({ ticket });
    if (apiKey) qs.set("api_key", apiKey);
    const url = resolveWs(`/ws?${qs.toString()}`);
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      // Server already starts the session implicitly; sending start_session
      // here just gets us a `session_started` echo to confirm.
      this.sendJson({ type: "start_session" });
      this.startKeepalive();
    };
    ws.onmessage = (ev) => this.onMessage(ev);
    ws.onclose = (ev) => {
      this.stopKeepalive();
      this.onEvent({ type: "closed", code: ev.code, reason: ev.reason ?? "" });
    };
    ws.onerror = () => {
      // The close handler fires right after with a meaningful code; nothing to
      // do here. (Browsers don't expose HTTP status on the upgrade failure.)
    };
    return true;
  }

  close(): void {
    this.stopKeepalive();
    try { this.ws?.close(1000, "client closing"); } catch { /* ignore */ }
    this.ws = null;
  }

  sendStartSession(): void { this.sendJson({ type: "start_session" }); }
  sendEndSession(): void { this.sendJson({ type: "end_session" }); }
  sendSetOtherLang(lang: string): void { this.sendJson({ type: "set_other_lang", lang }); }
  sendUttStart(utt_id: string): void {
    this.uttActive = true;
    this.sendJson({ type: "utt_start", utt_id, sr: 16000, codec: "pcm16" });
  }
  sendUttEnd(utt_id: string): void {
    this.uttActive = false;
    this.sendJson({ type: "utt_end", utt_id });
  }
  sendPcm(pcm: Int16Array): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    // ArrayBufferView view → binary frame. We slice to detach from a possibly
    // larger backing buffer (the mic worklet transfers the exact buffer, so
    // this is usually a no-op).
    this.ws.send(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength));
  }

  // --- internals --------------------------------------------------------
  private sendJson(obj: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(obj));
  }

  private onMessage(ev: MessageEvent): void {
    if (typeof ev.data === "string") {
      let parsed: { type?: string } & Record<string, unknown>;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }
      switch (parsed.type) {
        case "session_started":
          // First echo = genuine connect; later echoes are keepalive pings and
          // are swallowed so they don't self-heal the UI out of an error state.
          if (!this.connectAnnounced) {
            this.connectAnnounced = true;
            this.onEvent({ type: "connected", session_id: Number(parsed.session_id ?? -1) });
          }
          break;
        case "utt_accepted":
          this.onEvent({
            type: "utt_accepted",
            utt_id: String(parsed.utt_id ?? ""),
            utterance_id: Number(parsed.utterance_id ?? -1),
            turns: (parsed.turns as TurnSummary[]) ?? [],
          });
          break;
        case "partial":
          this.onEvent({
            type: "partial",
            role: String(parsed.role ?? ""),
            src_text: String(parsed.src_text ?? ""),
            translations: (parsed.translations as Translations) ?? {},
          });
          break;
        case "final":
          this.onEvent({
            type: "final",
            role: String(parsed.role ?? ""),
            turn_id: Number(parsed.turn_id ?? -1),
            src_text: String(parsed.src_text ?? ""),
            translations: (parsed.translations as Translations) ?? {},
            detected_language: (parsed.detected_language as string | null) ?? null,
            tts_pending: Boolean(parsed.tts_pending ?? false),
          });
          break;
        case "tts":
          this.pendingTts = {
            role: String(parsed.role ?? ""),
            turn_id: Number(parsed.turn_id ?? -1),
            byte_length: Number(parsed.byte_length ?? 0),
          };
          this.onEvent({ type: "tts_header", ...this.pendingTts });
          break;
        case "lang_changed":
          this.onEvent({ type: "lang_changed", current_other_lang: String(parsed.current_other_lang ?? "") });
          break;
        case "error":
          this.onEvent({ type: "error", code: String(parsed.code ?? ""), message: String(parsed.message ?? "") });
          break;
        case "session_ended":
          this.onEvent({ type: "session_ended", session_id: (parsed.session_id as number | null) ?? null });
          break;
        default:
          break;
      }
      return;
    }
    // Binary: paired with the most recent `tts` header.
    if (this.pendingTts) {
      const { role, turn_id } = this.pendingTts;
      this.pendingTts = null;
      this.onEvent({ type: "tts_audio", role, turn_id, wav: ev.data as ArrayBuffer });
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    // Server idle timeout defaults to 120 s. Ping every 75 s when no utterance
    // is active (PCM frames already reset the server's idle clock).
    this.keepaliveTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN && !this.uttActive) {
        this.sendJson({ type: "start_session" });
      }
    }, 75_000);
  }
  private stopKeepalive(): void {
    if (this.keepaliveTimer != null) {
      window.clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }
}

/** Translate a `closed` event into a user-facing message + retry policy. */
export { closeMessage } from "./ws-close";
